import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Card,
  Checkbox,
  ConfigProvider,
  Divider,
  Grid,
  Input,
  Radio,
  Space,
  Spin,
  Table,
  Typography,
  message,
} from 'antd';
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  FilePdfOutlined,
  PlusOutlined,
  ReloadOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import api from '../../../api/client';
import { formatNumeroCotizacion } from '../cotizaciones/numeroCotizacionUtils';

const { Title, Text } = Typography;

const mergeByKey = (defaults, incoming, keyField = 'key') => {
  if (!Array.isArray(incoming) || incoming.length === 0) return defaults;
  const map = new Map(incoming.map((r) => [r[keyField], r]));
  return defaults.map((d) => ({ ...d, ...(map.get(d[keyField]) || {}) }));
};

/** Textos por defecto en «Delegaciones ARCA» (editables / eliminables). */
export const DEFAULT_DELEGACIONES_ITEMS = [
  'GESTIÓN DE AUTORIZACIONES ELECTRÓNICAS / CREAR NUEVA AUTORIZACIÓN / DESPACHANTE AUTORIZACIÓN GENERAL-EXPORTACIÓN / AUTORIZADO: 20246179000 (JUAN MANUEL SAMBRANA)',
  'GESTIÓN DE AUTORIZACIONES ELECTRÓNICAS / CREAR NUEVA AUTORIZACIÓN / DESPACHANTE AUTORIZACIÓN GENERAL-EXPORTACIÓN / AUTORIZADO: 27287465209 (CECILIA BEATRIZ LUCERO)',
  'GESTIÓN DE AUTORIZACIONES ELECTRÓNICAS / CREAR NUEVA AUTORIZACIÓN / DESPACHANTE AUTORIZACIÓN GENERAL-IMPORTACIÓN / AUTORIZADO: 20246179000 (JUAN MANUEL SAMBRANA)',
  'GESTIÓN DE AUTORIZACIONES ELECTRÓNICAS / CREAR NUEVA AUTORIZACIÓN / DESPACHANTE AUTORIZACIÓN GENERAL-IMPORTACIÓN / AUTORIZADO: 27287465209 (CECILIA BEATRIZ LUCERO)',
  'GESTIÓN DE AUTORIZACIONES ELECTRÓNICAS / CREAR NUEVA AUTORIZACIÓN / AUTORIZADO POR EL IMPORTADOR-EXPORTADOR A SUSCRIBIR DOCUMENTACIÓN / AUTORIZADO: 20246179000 (JUAN MANUEL SAMBRANA)',
  'GESTIÓN DE AUTORIZACIONES ELECTRÓNICAS / CREAR NUEVA AUTORIZACIÓN / AUTORIZADO POR EL IMPORTADOR-EXPORTADOR A SUSCRIBIR DOCUMENTACIÓN / AUTORIZADO: 27287465209 (CECILIA BEATRIZ LUCERO)',
  'ADMINISTRADOR DE RELACIONES DE CLAVE FISCAL / NUEVA RELACION / ARCA / SERVICIOS INTERACTIVOS/COMPROBANTES EN LINEA / AUTORIZADO: 20246179000 (JUAN MANUEL SAMBRANA)',
  'ADMINISTRADOR DE RELACIONES DE CLAVE FISCAL / NUEVA RELACION / ARCA / SERVICIOS INTERACTIVOS/COMPROBANTES EN LINEA / AUTORIZADO: 20304339595 (DARIO MIGUEL FALVO)',
  'ADMINISTRADOR DE RELACIONES CON CLAVE FISCAL / NUEVA RELACIÓN / SENASA / SERVICIOS INTERACTIVOS/CERT-POV / AUTORIZADO: 20246179000 (JUAN MANUEL SAMBRANA)',
  'ADMINISTRADOR DE RELACIONES CON CLAVE FISCAL / NUEVA RELACIÓN / SENASA / SERVICIOS INTERACTIVOS/CERT-POV / AUTORIZADO: 20304339595 (DARIO MIGUEL FALVO)',
  'ADMINISTRADOR DE RELACIONES CON CLAVE FISCAL / NUEVA RELACIÓN / GESTIÓN DE SERVICIOS EXTRAORDINARIOS / AUTORIZADO: 20246179000 (JUAN MANUEL SAMBRANA)',
  'ADMINISTRADOR DE RELACIONES CON CLAVE FISCAL / NUEVA RELACIÓN / GESTIÓN DE SERVICIOS EXTRAORDINARIOS / AUTORIZADO: 27287465209 (CECILIA BEATRIZ LUCERO)',
  'ADMINISTRADOR DE RELACIONES DE CLAVE FISCAL / NUEVA RELACION / ARCA / SERVICIOS INTERACTIVOS/ GESTION DECLARACION JURADA VENTA AL EXTERIOR/ AUTORIZADO: 20246179000 (JUAN MANUEL SAMBRANA)',
  'ADMINISTRADOR DE RELACIONES DE CLAVE FISCAL / NUEVA RELACION / ARCA / SERVICIOS INTERACTIVOS/ GESTION DECLARACION JURADA VENTA AL EXTERIOR / AUTORIZADO: 27287465209 (CECILIA BEATRIZ LUCERO)',
];

/** Texto por defecto (editable); debe coincidir con `DEFAULT_CONDICIONES_PAGO_MONEDA` en el PDF del backend. */
export const DEFAULT_CONDICIONES_PAGO_MONEDA =
  'La factura se emite en dolares/ pesos según el concepto a facturar, durante la semana siguiente a la operación. ' +
  'Luego del vencimiento de la factura se aplican recargos por pagos fuera de termino. ' +
  'De aplicarse algún ajuste por tipo de cambio, sera detallado en la factura. ' +
  'Un cambio en las condiciones del servicio, supone un nuevo acuerdo, el cual sera enviado por mail, se considera aceptado al realizar la operacion inmediata posterior.';

const newDelegacionId = () => `dg-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const defaultDelegacionesRows = () =>
  DEFAULT_DELEGACIONES_ITEMS.map((texto, i) => ({ id: `def-${i}`, texto }));

/** DB/API: texto plano legacy o JSON `{ v:1, items: string[] }`. */
const condicionesEspecialesToItems = (raw) => {
  if (raw == null || String(raw).trim() === '') {
    return defaultDelegacionesRows();
  }
  try {
    const p = JSON.parse(raw);
    if (p && Array.isArray(p.items)) {
      if (p.items.length === 0) return [];
      return p.items.map((texto, i) => ({
        id: `row-${i}-${newDelegacionId()}`,
        texto: String(texto),
      }));
    }
  } catch {
    /* legacy texto plano */
  }
  return [{ id: 'legacy-1', texto: String(raw) }];
};

export const createEmptyCotizacionFinal = (opts = {}) => ({
  fecha: new Date().toISOString().slice(0, 10),
  numeroCotizacion: '',
  contactoGlobal: '',
  contactoCliente: '',
  telefono: '',
  razonSocial: '',
  cuit: '',
  adminNombre: '',
  adminTelefono: '',
  adminMail: '',
  gestiones: {
    inscripcionImpexp: false,
    registroCamara: false,
    habilitacionBalanza: false,
    hablExpl: false,
    hablExplTercero: false,
    renovacionBalanza: false,
  },
  tipoOperacion: opts.tipoOperacion ?? 'EXPORTACION',
  sectorDocComercial: [
    { key: 'SET_COMPLETO', label: 'SET COMPLETO', si: false, no: false, comentarios: '' },
    { key: 'ORIGEN', label: 'ORIGEN', si: false, no: false, comentarios: '' },
    { key: 'FITOSANITARIO', label: 'FITOSANITARIO', si: false, no: false, comentarios: '' },
    {
      key: 'ENVIO_MUESTRAS_DHL',
      label: 'ENVIO DE MUESTRAS CTA DHL GLOBAL',
      si: false,
      no: false,
      comentarios: '',
    },
    {
      key: 'ENVIO_DOC_DHL',
      label: 'ENVIO DE DOCUM. CTA DHL GLOBAL',
      si: false,
      no: false,
      comentarios: '',
    },
  ],
  otrosConceptos: [
    { key: 'LOGISTICA', label: 'LOGÍSTICA', si: false, no: false, comentarios: '' },
    { key: 'SIRA', label: 'SIRA', si: false, no: false, comentarios: '' },
    {
      key: 'OTROS_DESPACHANTES',
      label: 'OTROS DESPACHANTES',
      si: false,
      no: false,
      comentarios: '',
    },
  ],
  servicioAta: [
    { key: 'COBRAR_CLIENTE', label: 'COBRAR CLIENTE', si: false, no: false },
    { key: 'COBRAR_TRANSPORTE', label: 'COBRAR TRANSPORTE', si: false, no: false },
  ],
  delegacionesItems: defaultDelegacionesRows(),
  honorariosGlobal: '',
  condicionesPagoMoneda: DEFAULT_CONDICIONES_PAGO_MONEDA,
});

export const normalizeFromApi = (api, defaultTipoOperacion = 'EXPORTACION') => {
  const base = createEmptyCotizacionFinal({ tipoOperacion: defaultTipoOperacion });
  if (!api) return base;
  const { condicionesEspeciales: _ce, ...apiRest } = api;
  return {
    ...base,
    ...apiRest,
    condicionesPagoMoneda:
      api.condicionesPagoMoneda == null || String(api.condicionesPagoMoneda).trim() === ''
        ? DEFAULT_CONDICIONES_PAGO_MONEDA
        : String(api.condicionesPagoMoneda),
    fecha: api.fecha != null ? String(api.fecha).slice(0, 10) : base.fecha,
    tipoOperacion: api.tipoOperacion ?? defaultTipoOperacion,
    gestiones: { ...base.gestiones, ...(api.gestiones || {}) },
    sectorDocComercial: mergeByKey(base.sectorDocComercial, api.sectorDocComercial),
    otrosConceptos: mergeByKey(base.otrosConceptos, api.otrosConceptos),
    servicioAta: mergeByKey(base.servicioAta, api.servicioAta),
    delegacionesItems: condicionesEspecialesToItems(api.condicionesEspeciales),
  };
};

export const GESTIONES_LABELS = [
  ['inscripcionImpexp', 'Inscripción IMP/EXP'],
  ['registroCamara', 'Registro en Cámara'],
  ['habilitacionBalanza', 'Habilitación Balanza'],
  ['hablExpl', 'HABL EXPL'],
  ['hablExplTercero', 'HABL EXPL TERCERO'],
  ['renovacionBalanza', 'Renovación Balanza'],
];

const cantProductosEnPreCotizacion = (ini) => {
  if (Array.isArray(ini?.productos) && ini.productos.length > 0) return ini.productos.length;
  if (ini?.productoNombre) return 1;
  return 0;
};

const countProductosEnHonorarios = (text) => {
  const t = String(text || '').trim();
  if (!t) return 0;
  return (t.match(/^Producto:/gm) || []).length;
};

const countLineasConceptoEnTexto = (text) =>
  String(text || '')
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l && !/^Producto:/i.test(l)).length;

const countConceptosEnPreCotizacion = (ini) => {
  const bloques =
    Array.isArray(ini?.productos) && ini.productos.length > 0
      ? ini.productos
      : ini?.productoNombre
        ? [{ conceptos: ini.conceptos || [] }]
        : [];
  return bloques.reduce((n, b) => n + ((b.conceptos || []).length), 0);
};

/** Texto inicial para Honorarios Global: todos los productos y conceptos de la pre-cotización */
const buildHonorariosDesdePreCotizacion = (ini) => {
  const partes = [];
  const bloques =
    Array.isArray(ini.productos) && ini.productos.length > 0
      ? ini.productos
      : ini.productoNombre
        ? [{ productoNombre: ini.productoNombre, conceptos: ini.conceptos || [] }]
        : [];

  bloques.forEach((bloque, idx) => {
    if (bloque.productoNombre) {
      if (idx > 0) partes.push('');
      partes.push(`Producto: ${bloque.productoNombre}`);
    }
    (bloque.conceptos || []).forEach((linea) => {
      const titulo = linea.nombre || 'Concepto';
      const det = linea.detalle != null && String(linea.detalle).trim() ? String(linea.detalle).trim() : '';
      partes.push(det ? `${titulo}: ${det}` : titulo);
    });
  });
  return partes.join('\n').trim();
};

/**
 * Regenera honorarios si no hay texto, faltan productos, o el texto guardado tiene
 * menos conceptos que la cotización inicial (p. ej. carga vieja filtrada).
 */
const resolverHonorariosGlobal = (guardado, ini) => {
  const desdePre = buildHonorariosDesdePreCotizacion(ini);
  const cantPre = cantProductosEnPreCotizacion(ini);
  const guardadoTrim = String(guardado || '').trim();
  const cantGuardado = countProductosEnHonorarios(guardadoTrim);
  const conceptosPre = countConceptosEnPreCotizacion(ini);
  const conceptosGuardado = countLineasConceptoEnTexto(guardadoTrim);

  if (!guardadoTrim && desdePre) return desdePre;
  if (cantPre > 1 && cantGuardado < cantPre && desdePre) return desdePre;
  if (conceptosPre > 0 && conceptosGuardado < conceptosPre && desdePre) return desdePre;
  return guardadoTrim || desdePre;
};

const vacio = (v) => v == null || String(v).trim() === '';

/** Completa nombre/tel/mail de administración desde el ABM Cliente cuando el formulario no los tiene */
const mergeContactoAdminDesdeCliente = (base, cli) => {
  if (!cli) return base;
  return {
    ...base,
    adminNombre: vacio(base.adminNombre) ? (cli.adminNombre || '') : base.adminNombre,
    adminTelefono: vacio(base.adminTelefono) ? (cli.adminTelefono || '') : base.adminTelefono,
    adminMail: vacio(base.adminMail) ? (cli.adminMail || '') : base.adminMail,
  };
};

const CotizacionFinalForm = ({
  cotId,
  onBack,
  onSaved,
  defaultTipoOperacion = 'EXPORTACION',
  serviciosAcordadosDisabled = false,
}) => {
  const screens = Grid.useBreakpoint();
  const isNarrow = screens.md === false;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(() => createEmptyCotizacionFinal({ tipoOperacion: defaultTipoOperacion }));
  /** Para nombre de archivo al descargar PDF (expo/impo + número visible). */
  const [pdfMeta, setPdfMeta] = useState({ nro: null, tipoOperacion: defaultTipoOperacion });
  /** Pre-cotización para regenerar honorarios manualmente */
  const [preCotizacionRef, setPreCotizacionRef] = useState(null);

  const recargarHonorariosDesdePreCotizacion = useCallback(() => {
    if (!preCotizacionRef) return;
    const texto = buildHonorariosDesdePreCotizacion(preCotizacionRef);
    setForm((f) => ({ ...f, honorariosGlobal: texto }));
    message.success('Honorarios actualizados desde la cotización inicial');
  }, [preCotizacionRef]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [iniRes, finRes] = await Promise.all([
          api.get(`/cotizaciones/${cotId}`),
          api.get(`/cotizaciones/${cotId}/cotizacion-final`),
        ]);
        if (cancelled) return;
        const ini = iniRes.data;
        if (!ini.aprobada) {
          message.error('La cotizacion debe estar aprobada');
          onBack();
          return;
        }
        setPdfMeta({
          nro: formatNumeroCotizacion(ini.numeroSecuencial ?? ini.id),
          tipoOperacion: ini.tipoOperacion ?? defaultTipoOperacion,
        });
        const fin = finRes.data?.cotizacionFinal;
        setPreCotizacionRef(ini);
        const honorariosDesdePre = buildHonorariosDesdePreCotizacion(ini);
        const nroCotizacion = formatNumeroCotizacion(ini.numeroSecuencial ?? ini.id);

        let clienteDetalle = null;
        if (ini.numeroCliente != null) {
          try {
            const { data } = await api.get(`/clientes/${ini.numeroCliente}`);
            clienteDetalle = data;
          } catch {
            clienteDetalle = null;
          }
        }

        if (fin) {
          let merged = normalizeFromApi(fin, defaultTipoOperacion);
          if (serviciosAcordadosDisabled && defaultTipoOperacion === 'IMPORTACION') {
            merged = { ...merged, tipoOperacion: 'IMPORTACION' };
          }
          merged = {
            ...merged,
            numeroCotizacion: nroCotizacion,
            honorariosGlobal: resolverHonorariosGlobal(merged.honorariosGlobal, ini),
          };
          setForm(mergeContactoAdminDesdeCliente(merged, clienteDetalle));
        } else {
          setForm(
            mergeContactoAdminDesdeCliente(
              {
                ...createEmptyCotizacionFinal({ tipoOperacion: defaultTipoOperacion }),
                razonSocial: ini.clienteNombre || '',
                cuit: ini.cuit || '',
                numeroCotizacion: nroCotizacion,
                honorariosGlobal: honorariosDesdePre,
              },
              clienteDetalle
            )
          );
        }
      } catch (e) {
        if (!cancelled) {
          message.error(e?.response?.data?.message || 'Error al cargar datos');
          onBack();
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [cotId, onBack, defaultTipoOperacion, serviciosAcordadosDisabled]);

  const setGest = (key, value) => {
    setForm((f) => ({ ...f, gestiones: { ...f.gestiones, [key]: value } }));
  };

  const patchRow = (section, index, patch) => {
    setForm((f) => {
      const arr = [...f[section]];
      arr[index] = { ...arr[index], ...patch };
      return { ...f, [section]: arr };
    });
  };

  const actualizarDelegacion = (id, texto) => {
    setForm((f) => ({
      ...f,
      delegacionesItems: (f.delegacionesItems || []).map((r) => (r.id === id ? { ...r, texto } : r)),
    }));
  };

  const eliminarDelegacion = (id) => {
    setForm((f) => ({
      ...f,
      delegacionesItems: (f.delegacionesItems || []).filter((r) => r.id !== id),
    }));
  };

  const agregarDelegacion = () => {
    setForm((f) => ({
      ...f,
      delegacionesItems: [...(f.delegacionesItems || []), { id: newDelegacionId(), texto: '' }],
    }));
  };

  const guardar = async () => {
    setSaving(true);
    try {
      const { delegacionesItems: delItems, ...formRest } = form;
      const condicionesEspeciales = JSON.stringify({
        v: 1,
        items: (delItems || []).map((r) => r.texto),
      });
      const nroFijo = formatNumeroCotizacion(
        preCotizacionRef?.numeroSecuencial ?? form.numeroCotizacion ?? cotId
      );
      let payload = { ...formRest, condicionesEspeciales, numeroCotizacion: nroFijo };
      if (serviciosAcordadosDisabled && defaultTipoOperacion === 'IMPORTACION') {
        payload = { ...payload, tipoOperacion: 'IMPORTACION' };
      }
      await api.put(`/cotizaciones/${cotId}/cotizacion-final`, payload);
      message.success('Cotizacion final guardada');
      onSaved?.();
    } catch (e) {
      message.error(e?.response?.data?.message || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const descargarPdf = useCallback(async () => {
    try {
      const res = await api.get(`/cotizaciones/${cotId}/cotizacion-final/pdf`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      const tag = pdfMeta.tipoOperacion === 'IMPORTACION' ? 'impo' : 'expo';
      const n = pdfMeta.nro ?? cotId;
      a.download = `cotizacion-final-${tag}-${n}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      message.error(e?.response?.data?.message || 'Error al generar PDF (guarde antes si aun no lo hizo)');
    }
  }, [cotId, pdfMeta]);

  /** Tablas SI/NO/comentarios: deshabilitadas en flujo importación o si el usuario elige «Importación» en Exportación. */
  const tablasServiciosDisabled =
    serviciosAcordadosDisabled || form.tipoOperacion === 'IMPORTACION';

  const colsDoc = (section) => [
    {
      title: 'Concepto',
      dataIndex: 'label',
      width: 260,
      render: (t) => <Text strong>{t}</Text>,
    },
    {
      title: 'SI',
      width: 56,
      align: 'center',
      render: (_, __, i) => (
        <Checkbox
          disabled={tablasServiciosDisabled}
          checked={form[section][i].si}
          onChange={(e) => {
            const v = e.target.checked;
            patchRow(section, i, { si: v, no: v ? false : form[section][i].no });
          }}
        />
      ),
    },
    {
      title: 'NO',
      width: 56,
      align: 'center',
      render: (_, __, i) => (
        <Checkbox
          disabled={tablasServiciosDisabled}
          checked={form[section][i].no}
          onChange={(e) => {
            const v = e.target.checked;
            patchRow(section, i, { no: v, si: v ? false : form[section][i].si });
          }}
        />
      ),
    },
    {
      title: 'Comentarios',
      render: (_, __, i) => (
        <Input
          disabled={tablasServiciosDisabled}
          value={form[section][i].comentarios}
          onChange={(e) => patchRow(section, i, { comentarios: e.target.value })}
          placeholder="Opcional"
        />
      ),
    },
  ];

  const colsAta = [
    {
      title: 'Concepto',
      dataIndex: 'label',
      width: 260,
      render: (t) => <Text strong>{t}</Text>,
    },
    {
      title: 'SI',
      width: 56,
      align: 'center',
      render: (_, __, i) => (
        <Checkbox
          disabled={tablasServiciosDisabled}
          checked={form.servicioAta[i].si}
          onChange={(e) => {
            const v = e.target.checked;
            patchRow('servicioAta', i, { si: v, no: v ? false : form.servicioAta[i].no });
          }}
        />
      ),
    },
    {
      title: 'NO',
      width: 56,
      align: 'center',
      render: (_, __, i) => (
        <Checkbox
          disabled={tablasServiciosDisabled}
          checked={form.servicioAta[i].no}
          onChange={(e) => {
            const v = e.target.checked;
            patchRow('servicioAta', i, { no: v, si: v ? false : form.servicioAta[i].si });
          }}
        />
      ),
    },
  ];

  return (
    <Spin spinning={loading}>
      <Card
        title={
          <Space>
            <Button type="text" icon={<ArrowLeftOutlined />} onClick={onBack}>
              Volver
            </Button>
            <Title level={5} style={{ margin: 0, color: '#198039' }}>
              Cotizacion final — Condiciones de venta (COM-P-01)
            </Title>
          </Space>
        }
        extra={
          <Space wrap>
            <Button icon={<FilePdfOutlined />} onClick={() => void descargarPdf()}>
              PDF
            </Button>
            <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={() => void guardar()}>
              Guardar
            </Button>
          </Space>
        }
        style={{ borderColor: '#d8e8dc', background: '#ffffff' }}
        styles={{ body: { padding: isNarrow ? 12 : 16 } }}
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          Complete el formulario según el acuerdo con el cliente. Podrá imprimir un PDF con el mismo contenido.
        </Text>

        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Card size="small" title="Datos generales" style={{ borderColor: '#d8e8dc' }}>
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <Space wrap>
                <div>
                  <Text strong style={{ display: 'block', marginBottom: 4 }}>
                    Fecha
                  </Text>
                  <Input
                    type="date"
                    value={form.fecha || ''}
                    onChange={(e) => setForm((f) => ({ ...f, fecha: e.target.value }))}
                    style={{ width: 160 }}
                  />
                </div>
                <div>
                  <Text strong style={{ display: 'block', marginBottom: 4 }}>
                    Cotización N°
                  </Text>
                  <Input
                    value={form.numeroCotizacion}
                    readOnly
                    disabled
                    style={{ width: 120 }}
                    title="Se toma del Nº de la cotización; editalo desde la grilla o Acciones"
                  />
                </div>
              </Space>
              <div>
                <Text strong style={{ display: 'block', marginBottom: 4 }}>
                  Contacto Global
                </Text>
                <Input
                  value={form.contactoGlobal}
                  onChange={(e) => setForm((f) => ({ ...f, contactoGlobal: e.target.value }))}
                  placeholder="Nombre"
                />
              </div>
              <div>
                <Text strong style={{ display: 'block', marginBottom: 4 }}>
                  Contacto Cliente
                </Text>
                <Input
                  value={form.contactoCliente}
                  onChange={(e) => setForm((f) => ({ ...f, contactoCliente: e.target.value }))}
                />
              </div>
              <div>
                <Text strong style={{ display: 'block', marginBottom: 4 }}>
                  Teléfono
                </Text>
                <Input
                  value={form.telefono}
                  onChange={(e) => setForm((f) => ({ ...f, telefono: e.target.value }))}
                />
              </div>
            </Space>
          </Card>

          <Card size="small" title="Datos cliente" style={{ borderColor: '#d8e8dc' }}>
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <div>
                <Text strong style={{ display: 'block', marginBottom: 4 }}>
                  Razón social
                </Text>
                <Input
                  value={form.razonSocial}
                  onChange={(e) => setForm((f) => ({ ...f, razonSocial: e.target.value }))}
                />
              </div>
              <div>
                <Text strong style={{ display: 'block', marginBottom: 4 }}>
                  N° CUIT
                </Text>
                <Input value={form.cuit} onChange={(e) => setForm((f) => ({ ...f, cuit: e.target.value }))} />
              </div>
              <Divider style={{ margin: '8px 0' }}>Contacto administración y facturación</Divider>
              <div>
                <Text strong style={{ display: 'block', marginBottom: 4 }}>
                  Nombre
                </Text>
                <Input
                  value={form.adminNombre}
                  onChange={(e) => setForm((f) => ({ ...f, adminNombre: e.target.value }))}
                />
              </div>
              <div>
                <Text strong style={{ display: 'block', marginBottom: 4 }}>
                  Teléfono
                </Text>
                <Input
                  value={form.adminTelefono}
                  onChange={(e) => setForm((f) => ({ ...f, adminTelefono: e.target.value }))}
                />
              </div>
              <div>
                <Text strong style={{ display: 'block', marginBottom: 4 }}>
                  Mail
                </Text>
                <Input
                  value={form.adminMail}
                  onChange={(e) => setForm((f) => ({ ...f, adminMail: e.target.value }))}
                />
              </div>
            </Space>
          </Card>

          <Card size="small" title="Gestiones solicitadas por el cliente" style={{ borderColor: '#d8e8dc' }}>
            <Space direction="vertical" size="small">
              {GESTIONES_LABELS.map(([key, label]) => (
                <Checkbox
                  key={key}
                  checked={form.gestiones[key]}
                  onChange={(e) => setGest(key, e.target.checked)}
                >
                  {label}
                </Checkbox>
              ))}
            </Space>
          </Card>

          <Card size="small" title="Servicios acordados" style={{ borderColor: '#d8e8dc' }}>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>
              Operación
            </Text>
            <ConfigProvider
              theme={{
                token: { colorPrimary: '#198039' },
                components: {
                  Radio: {
                    buttonBg: '#ffffff',
                    buttonCheckedBg: '#f0faf2',
                    buttonCheckedColor: '#198039',
                    buttonCheckedBorderColor: '#198039',
                    buttonColor: 'rgba(0,0,0,0.88)',
                    colorBorder: '#d9d9d9',
                  },
                },
              }}
            >
              <Radio.Group
                optionType="button"
                buttonStyle="outline"
                disabled={serviciosAcordadosDisabled}
                value={form.tipoOperacion ?? defaultTipoOperacion}
                onChange={(e) => setForm((f) => ({ ...f, tipoOperacion: e.target.value }))}
              >
                <Radio.Button value="IMPORTACION">Importación</Radio.Button>
                <Radio.Button value="EXPORTACION">Exportación</Radio.Button>
              </Radio.Group>
            </ConfigProvider>
            <Divider />
            <Text strong>Sector documentación comercial</Text>
            <Table
              size="small"
              pagination={false}
              rowKey="key"
              columns={colsDoc('sectorDocComercial')}
              dataSource={form.sectorDocComercial}
              scroll={{ x: 600 }}
            />
            <Divider />
            <Text strong>Otros conceptos a cobrar</Text>
            <Table
              size="small"
              pagination={false}
              rowKey="key"
              columns={colsDoc('otrosConceptos')}
              dataSource={form.otrosConceptos}
              scroll={{ x: 600 }}
            />
            <Divider />
            <Text strong>Servicio ATA</Text>
            <Table
              size="small"
              pagination={false}
              rowKey="key"
              columns={colsAta}
              dataSource={form.servicioAta}
              scroll={{ x: 500 }}
            />
          </Card>

          <Card size="small" title="Condiciones especiales" style={{ borderColor: '#d8e8dc' }}>
            <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
              Delegaciones ARCA: cada línea puede editarse, eliminarse o agregar nuevas.
            </Text>
            <Text strong style={{ display: 'block', marginBottom: 10, fontSize: 12 }}>
              *Delegaciones que debe hacernos desde ARCA:
            </Text>
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              {(form.delegacionesItems || []).map((row) => (
                <div
                  key={row.id}
                  style={{
                    display: 'flex',
                    gap: 8,
                    alignItems: 'flex-start',
                    width: '100%',
                  }}
                >
                  <Input.TextArea
                    autoSize={{ minRows: 2, maxRows: 8 }}
                    value={row.texto}
                    onChange={(e) => actualizarDelegacion(row.id, e.target.value)}
                    placeholder="Texto de la delegación"
                    style={{ flex: 1, minWidth: 0 }}
                  />
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => eliminarDelegacion(row.id)}
                    aria-label="Quitar línea"
                  />
                </div>
              ))}
              <Button type="dashed" icon={<PlusOutlined />} onClick={agregarDelegacion} block>
                Agregar delegación
              </Button>
            </Space>
          </Card>

          <Card size="small" title="Honorarios Global" style={{ borderColor: '#d8e8dc' }}>
            <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
              Información disponible solamente para administración
            </Text>
            <Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>
              Al cargar por primera vez se incorporan todos los productos y el detalle de todos los
              conceptos de la cotización inicial. Si agregó productos o conceptos después, use el
              botón para volver a cargar desde la cotización.
            </Text>
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Button
                type="default"
                size="small"
                icon={<ReloadOutlined />}
                onClick={recargarHonorariosDesdePreCotizacion}
              >
                Actualizar desde cotización inicial
              </Button>
              <Input.TextArea
                rows={Math.min(20, Math.max(6, countProductosEnHonorarios(form.honorariosGlobal) * 5))}
                value={form.honorariosGlobal}
                onChange={(e) => setForm((f) => ({ ...f, honorariosGlobal: e.target.value }))}
              />
            </Space>
          </Card>

          <Card size="small" title="Condiciones de pago / moneda" style={{ borderColor: '#d8e8dc' }}>
            <Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>
              Texto estándar precargado; puede editarlo o reemplazarlo cuando corresponda.
            </Text>
            <Input.TextArea
              rows={8}
              value={form.condicionesPagoMoneda}
              onChange={(e) => setForm((f) => ({ ...f, condicionesPagoMoneda: e.target.value }))}
            />
          </Card>

          <Button type="primary" size="large" icon={<SaveOutlined />} loading={saving} onClick={() => void guardar()}>
            Guardar cotizacion final
          </Button>
        </Space>
      </Card>
    </Spin>
  );
};

export default CotizacionFinalForm;
